import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

export interface StaffMember {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  role: string;
  is_active: boolean;
  profile_picture: string | null;
  invite_accepted: boolean;
  created_at: string;
  updated_at: string;
  company_count: number;
}

export interface StaffDetail extends StaffMember {
  last_login_at: string | null;
  profile_image_url?: string | null;
  phone?: string | null;
  job_title?: string | null;
  department?: string | null;
  bio?: string | null;
  assignments: {
    id: number;
    business_id: number;
    business_name: string;
    role: string;
    permissions: Record<string, boolean>;
    assigned_at: string;
  }[];
}

export interface StaffProfileUpdateData {
  name?: string;
  phone?: string | null;
  jobTitle?: string | null;
  department?: string | null;
  bio?: string | null;
}

export interface CreateStaffData {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  companyIds?: number[];
}

// List all staff for tenant
export function useStaffList() {
  return useQuery<StaffMember[]>({
    queryKey: ["/api/staff"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/staff");
      if (!res.ok) throw new Error("Failed to fetch staff");
      return res.json();
    },
  });
}

// Get staff detail
export function useStaffDetail(staffId: number | null) {
  return useQuery<StaffDetail>({
    queryKey: ["/api/staff", staffId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/staff/${staffId}`);
      if (!res.ok) throw new Error("Failed to fetch staff details");
      return res.json();
    },
    enabled: !!staffId,
  });
}

// Create staff
export function useCreateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateStaffData) => {
      const res = await apiRequest("POST", "/api/staff", data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to create staff");
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/staff"] }),
  });
}

// Update staff
export function useUpdateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: number } & Record<string, any>) => {
      const res = await apiRequest("PATCH", `/api/staff/${id}`, data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to update staff");
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/staff"] }),
  });
}

// Deactivate staff
export function useDeactivateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("DELETE", `/api/staff/${id}`);
      if (!res.ok) throw new Error("Failed to deactivate staff");
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/staff"] }),
  });
}

// Reactivate staff
export function useReactivateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("PATCH", `/api/staff/${id}`, { isActive: true });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to reactivate staff");
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/staff"] }),
  });
}

// Reset password
export function useResetStaffPassword() {
  return useMutation({
    mutationFn: async ({ id, password }: { id: number; password: string }) => {
      const res = await apiRequest("PATCH", `/api/staff/${id}/password`, { password });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to reset password");
      }
      return res.json();
    },
  });
}

// Assign companies to staff
export function useAssignCompanies() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ staffId, companyIds }: { staffId: number; companyIds: number[] }) => {
      const res = await apiRequest("POST", `/api/staff/${staffId}/companies`, { companyIds });
      if (!res.ok) throw new Error("Failed to assign companies");
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/staff"] }),
  });
}

// ── STAFF PROFILE HOOKS ───────────────────────────────────────────────

// Fetch a staff profile (full detail incl. image)
export function useStaffProfile(staffId: number | null) {
  return useQuery<StaffDetail>({
    queryKey: ["staff-profile", staffId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/staff/profile/${staffId}`);
      if (!res.ok) throw new Error("Failed to fetch staff profile");
      return res.json();
    },
    enabled: !!staffId,
  });
}

// Update a staff profile (name, phone, jobTitle, department, bio)
export function useUpdateStaffProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ staffId, data }: { staffId: number; data: StaffProfileUpdateData }) => {
      console.log("[StaffProfile] PATCH /api/staff/profile", { staffId, data });
      const res = await apiRequest("PATCH", `/api/staff/profile?staffId=${staffId}`, data);
      return res.json();
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["staff"] });
      qc.invalidateQueries({ queryKey: ["/api/staff"] });
      qc.invalidateQueries({ queryKey: ["/api/staff", vars.staffId] });
      qc.invalidateQueries({ queryKey: ["staff-profile", vars.staffId] });
    },
  });
}

// Upload profile image
export function useUploadStaffProfileImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ staffId, file }: { staffId: number; file: File }) => {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiRequest("POST", `/api/staff/${staffId}/profile-image`, formData);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to upload image");
      }
      return res.json() as Promise<{ success: boolean; url: string }>;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["staff"] });
      qc.invalidateQueries({ queryKey: ["/api/staff"] });
      qc.invalidateQueries({ queryKey: ["staff-profile", vars.staffId] });
    },
  });
}

// Get plan limit info
export function useStaffPlanLimit() {
  return useQuery<{ allowed: boolean; current: number; max: number }>({
    queryKey: ["/api/staff/plan-limit"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/staff/plan-limit");
      if (!res.ok) return { allowed: true, current: 0, max: 999 };
      return res.json();
    },
  });
}
